import React, { useState } from "react";
import "./BlsIntroductionRow.scss";

const money = (value) => {
  const n = Number(value);
  return n > 0 ? `$${n.toLocaleString("en-US")}` : null;
};

const count = (value, one, many) => {
  const n = Number(value);
  if (!n) return null;
  const shown = Number.isInteger(n) ? n : Number(n.toFixed(1));
  return `${shown} ${shown === 1 ? one : many}`;
};

export const introHeadline = (intro) => {
  const isMlsListing = !intro?.Inv_Id;
  const address = String(intro?.Location || "").trim();
  if (isMlsListing && address) return address;
  const place = [intro?.City, intro?.State].filter(Boolean).join(", ");
  const type = intro?.Sub_PropertyTypeText || intro?.PropertyTypeText || "A home";
  return place ? `${type} in ${place}` : type;
};

export const introFacts = (intro) =>
  [
    money(intro?.AskingPrice),
    count(intro?.Bedrooms, "bed", "beds"),
    count(intro?.Bathrooms, "bath", "baths"),
    Number(intro?.Sqft) > 0 ? `${Number(intro.Sqft).toLocaleString("en-US")} sqft` : null,
  ].filter(Boolean);

export const sentAgo = (createdOn, now = Date.now()) => {
  const sent = new Date(createdOn).getTime();
  if (!sent) return null;
  const days = Math.floor((now - sent) / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return `on ${new Date(sent).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

const initialsOf = (intro) =>
  `${String(intro?.FirstName || "").trim().charAt(0)}${String(intro?.LastName || "").trim().charAt(0)}`.toUpperCase();

export const senderSource = (intro, network = "MLS") => {
  if (Number(intro?.SenderUserTypeId) !== 2) return "homeowner on Gitcha";
  return Number(intro?.SenderInNetwork) === 1 ? `${network} member` : "agent on Gitcha";
};

const HouseMark = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3.5 10.5 12 4l8.5 6.5V20h-5.5v-5.5h-6V20H3.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const BlsIntroductionRow = ({ intro, onView, onMessage, onNotFit, accepting, busy, network, showListing = false }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = String(intro?.Photo_1 || "").trim();
  const sender = [intro?.FirstName, intro?.LastName].filter(Boolean).join(" ");
  const facts = introFacts(intro);

  const when = sentAgo(intro?.CreatedOn);
  const isNew = Number(intro?.ReadStatus) === 0;

  return (
    <article className={`bls-intro-row${isNew ? " bls-intro-row--new" : ""}`}>
      <button type="button" className="bls-intro-row__main" onClick={onView} disabled={busy}>
        <span className="bls-intro-row__photo">
          {photo && !photoFailed ? (
            <img src={photo} alt="" onError={() => setPhotoFailed(true)} />
          ) : (
            <HouseMark />
          )}
        </span>
        <span className="bls-intro-row__text">
          {showListing && intro?.Title_Text ? (
            <span className="bls-intro-row__for">For {intro.Title_Text}</span>
          ) : null}
          <span className="bls-intro-row__headline">
            {introHeadline(intro)}
            {isNew ? <span className="bls-intro-row__new-dot" aria-label="New" /> : null}
          </span>
          {facts.length ? (
            <span className="bls-intro-row__facts">
              <span className="bls-intro-row__facts-line">
                {facts.map((fact) => (
                  <span key={fact}>
                    <span className="bls-intro-row__dot" aria-hidden="true">·</span>
                    {fact}
                  </span>
                ))}
              </span>
            </span>
          ) : null}
          {sender ? (
            <span className="bls-intro-row__sender">
              <span className="bls-intro-row__avatar" aria-hidden="true">
                {initialsOf(intro)}
              </span>
              <span>
                <strong>{sender}</strong>, {senderSource(intro, network)}
                {when ? <span className="bls-intro-row__when"> · sent {when}</span> : null}
              </span>
            </span>
          ) : null}
        </span>
      </button>
      <div className="bls-intro-row__actions">
        <button type="button" className="bls-intro-row__link" onClick={onNotFit} disabled={busy}>
          Not a fit
        </button>
        <button type="button" className="bls-intro-row__secondary" onClick={onView} disabled={busy}>
          {intro?.Inv_Id ? "View home" : "View listing"}
        </button>
        <button type="button" className="bls-intro-row__primary" onClick={onMessage} disabled={busy}>
          {accepting ? "Opening..." : `Message ${intro?.FirstName || "agent"}`}
        </button>
      </div>
    </article>
  );
};

export default BlsIntroductionRow;
